import React from 'react';
import { Trans } from 'react-i18next';
import { call, all, put, take, race, select } from 'redux-saga/effects';
import { request } from '../../xhr';
import { actions as StreamActions } from '../../Stream';
import { getCurrentStreamId, getCurrentGallection, getCurrentSubfolderId, getGallectionById } from '../selectors';
import { deselectAll } from '../actions';
import setGallectionCover from './setGallectionCover';
import updateGallectionSize from './updateGallectionSize';
import { confirmYes, confirmNo, pushGlobalConfirmationModalForMM } from '../../Modals/redux/actionCreators';
import { isPremiumGallection, getPremiumGallectionType } from '../../utils/gallections';
import { Warning } from '@wix/da-ds/pkg/Icons/Confirmations';
import { getDeviationEntityIds } from '../../redux/normalizr/schemas/deviation';
import { checkAndWarnIfDeviationsAreNotSupportedForPremium } from './helpers';
import { getGallectionType } from '../helpers';
export default function*(action) {
    const { targetFolderId , deviations  } = action.payload;
    const { streamId , currentGallection , targetGallection , currentSubfolderId  } = yield select((state)=>({
            streamId: getCurrentStreamId(state),
            currentGallection: getCurrentGallection(state),
            currentSubfolderId: getCurrentSubfolderId(state),
            targetGallection: getGallectionById(state, targetFolderId)
        }));
    if (!currentGallection || !targetGallection) {
        return;
    }
    const isFromPremiumFolder = isPremiumGallection(currentGallection);
    const isToPremiumFolder = isPremiumGallection(targetGallection);
    let confirmationTextTransKey, confirmationTitleTransKey;
    if (isFromPremiumFolder || isToPremiumFolder) {
        const fromType = getPremiumGallectionType(currentGallection) || 'regular';
        const toType = getPremiumGallectionType(targetGallection) || 'regular';
        // Moving between WO folders is permitted
        if (fromType === 'watchers' && toType === 'watchers') {
            return;
        }
        const confirmationTitleTransMap = {
            paid: {
                paid: 'mediapopup.confirmationPopup.moveDeviation.title.fromPaidToPaid',
                watchers: 'mediapopup.confirmationPopup.moveDeviation.title.fromPaidToWatchersOnly',
                regular: 'mediapopup.confirmationPopup.moveDeviation.title.fromPaidToRegular'
            },
            watchers: {
                paid: 'mediapopup.confirmationPopup.moveDeviation.title.fromWatchersOnlyToPaid',
                regular: 'mediapopup.confirmationPopup.moveDeviation.title.fromWatchersOnlyToRegular'
            },
            regular: {
                paid: 'mediapopup.confirmationPopup.moveDeviation.title.fromRegularToPaid',
                watchers: 'mediapopup.confirmationPopup.moveDeviation.title.fromRegularToWatchersOnly'
            }
        };
        const confirmationTextTransMap = {
            paid: {
                paid: 'mediapopup.confirmationPopup.moveDeviation.text.fromPaidToPaid',
                watchers: 'mediapopup.confirmationPopup.moveDeviation.text.fromPaidToWatchersOnly',
                regular: 'mediapopup.confirmationPopup.moveDeviation.text.fromPaidToRegular'
            },
            watchers: {
                paid: 'mediapopup.confirmationPopup.moveDeviation.text.fromWatchersOnlyToPaid',
                regular: 'mediapopup.confirmationPopup.moveDeviation.text.fromWatchersOnlyToRegular'
            },
            regular: {
                paid: /*#__PURE__*/ React.createElement(Trans, {
                    i18nKey: "mediapopup.confirmationPopup.moveDeviation.text.fromRegularToPaid"
                }, "• Only your paying Supporters will have access to it.", /*#__PURE__*/ React.createElement("br", null), "• It will become locked for all your non-Supporters and will affect Collections, Groups, Profile Spotlights,", ' ', /*#__PURE__*/ React.createElement("a", {
                    href: "https://www.deviantartsupport.com/en/article/what-happens-when-i-move-deviations-in-or-out-of-premium-galleries"
                }, "and more"), ".", /*#__PURE__*/ React.createElement("br", null), "• Prints and Premium Downloads will be disabled.", /*#__PURE__*/ React.createElement("br", null), /*#__PURE__*/ React.createElement("br", null), "Are you sure you want to move this deviation?"),
                watchers: /*#__PURE__*/ React.createElement(Trans, {
                    i18nKey: "mediapopup.confirmationPopup.moveDeviation.text.fromRegularToWatchersOnly"
                }, "• Only your watchers will have access to it.", /*#__PURE__*/ React.createElement("br", null), "• It will become locked for deviants who don’t watch you and will affect Collections, Groups, Profile Spotlights,", ' ', /*#__PURE__*/ React.createElement("a", {
                    href: "https://www.deviantartsupport.com/en/article/what-happens-when-i-move-deviations-in-or-out-of-premium-galleries"
                }, "and more"), ".", /*#__PURE__*/ React.createElement("br", null), "• Prints and Premium Downloads will be disabled.", /*#__PURE__*/ React.createElement("br", null), /*#__PURE__*/ React.createElement("br", null), "Are you sure you want to move this deviation?")
            }
        };
        confirmationTitleTransKey = confirmationTitleTransMap[fromType][toType];
        confirmationTextTransKey = confirmationTextTransMap[fromType][toType];
        // Attempts to move unsupported deviations
        // into a PF will override the above message
        if (isToPremiumFolder) {
            const hasUnsupportedDeviationSelected = yield call(checkAndWarnIfDeviationsAreNotSupportedForPremium, deviations);
            if (hasUnsupportedDeviationSelected) {
                confirmationTitleTransKey = 'mediapopup.confirmationPopup.moveDeviation.title.unsupported';
                confirmationTextTransKey = 'mediapopup.confirmationPopup.moveDeviation.text.unsupported';
            }
        }
        yield put(pushGlobalConfirmationModalForMM({
            titleTransKey: confirmationTitleTransKey,
            textTransKey: confirmationTextTransKey,
            confirmBtnLabelTransKey: 'mediapopup.confirmationPopup.moveDeviation.confirm',
            cancelBtnLabelTransKey: 'popup.cancel',
            icon: /*#__PURE__*/ React.createElement(Warning, null)
        }));
        const { no  } = yield race({
            yes: take(confirmYes),
            no: take(confirmNo)
        });
        if (no) {
            return;
        }
    }
    const entityIds = getDeviationEntityIds(deviations);
    yield all([
        put(StreamActions.deleteItems({
            streamId,
            items: entityIds
        })),
        put(deselectAll())
    ]);
    yield call(setGallectionCover, {
        gallection: targetGallection,
        deviation: deviations[0],
        onlyIfMissing: true
    });
    yield call(updateGallectionSize, targetFolderId, deviations.length);
    const deviationIds = deviations.map((deviation)=>deviation.deviationId);
    var _getGallectionType;
    const params = {
        type: (_getGallectionType = getGallectionType(currentGallection)) !== null && _getGallectionType !== void 0 ? _getGallectionType : 'collection',
        src_folderid: currentSubfolderId || currentGallection.folderId,
        target_folderid: targetFolderId,
        deviationids: deviationIds
    };
    yield call(request, {
        method: 'post',
        url: 'gallection/folders/move_deviations',
        data: params
    });
}
